import { BrowserUtils } from "@rsm-hcd/javascript-core";
import StickyNotification from "atoms/sticky-notification/sticky-notification";
import { LayoutGroup, motion } from "framer-motion";
import PublicationRecord from "models/view-models/publication-record";
import ErrorBoundary from "molecules/errors/error-boundary";
import BreadcrumbsMenu from "organisms/breadcrumbs/breadcrumbs-menu";
import LegacyPublicationContent from "organisms/publication-content/legacy-publication-content";
import React, { useEffect } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import useLegacyPublication from "utilities/hooks/domain/publications/legacy/use-legacy-publication";
import { t } from "utilities/localization-utils";
import StringUtils from "utilities/string-utils";
import { useSetCurrentPdfPublication } from "organisms/table-of-contents/mobile-table-of-contents-button";
import BookviewSearchbar from "organisms/panels/bookview-tab-panels/bookview-search/bookview-search-bar";
import { BookviewSearchProvider } from "utilities/contexts/bookview-tab-panel/bookview-search-provider";
import {
    EventsProvider,
    SearchSource,
} from "utilities/contexts/events/events-provider";
import { useBookviewTabPanel } from "utilities/contexts/bookview-tab-panel/use-bookview-tab-panel";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { Icons } from "atoms/constants/icons";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ButtonSizes } from "atoms/constants/button-sizes";
import MobileSecondaryNavLegacyPublications from "organisms/mobile-secondary-nav/mobile-secondary-nav-legacy-publications";
import { useMobileSecondaryNavContent } from "utilities/contexts/use-mobile-secondary-nav/use-mobile-secondary-nav-content";
import { CollectionUtils } from "utilities/collection-utils";
import useFeatureFlags from "utilities/hooks/use-feature-flags";

const CSS_BASE_CLASS = "c-legacy-publication-page";
const CSS_PAGE_CLASS = "c-image-viewer";

interface LegacyPublicationPageProps {
    publication: PublicationRecord;
}

const LegacyPublicationPage: React.FC<LegacyPublicationPageProps> = (
    props: LegacyPublicationPageProps
) => {
    const { publication } = props;
    const { globalState } = useGlobalState();
    const { currentIdentity } = globalState;
    const { isBookviewSearchPanelOpen, isOpen } = useBookviewTabPanel();
    const {
        isBookviewSearchPanelOpen: isMobileBookviewSearchPanelOpen,
        toggleBookviewSearchPanel: toggleMobileBookviewSearchPanel,
    } = useMobileSecondaryNavContent();
    const { inMobileView } = useBreakpointRanges();

    const { allowLegacyPublicationSectionFiltering } = useFeatureFlags();

    const {
        resultObject: legacyPublication,
        loading,
        loaded,
        errors,
    } = useLegacyPublication({
        publicationId: publication.id!,
    });

    const showBookviewSearchBreadcrumbOptions =
        CollectionUtils.hasValues(legacyPublication.tableOfContents) &&
        allowLegacyPublicationSectionFiltering;

    const { setPublication } = useSetCurrentPdfPublication();

    useEffect(() => {
        if (publication.isPersisted() && loaded) {
            setPublication(
                legacyPublication.fromPublicationRecord(publication)
            );
        }

        return () => {
            setPublication(null);
        };
    }, [legacyPublication, loaded, publication, setPublication]);

    const renderUnpublishedSticky =
        currentIdentity?.isAdmin() &&
        !loading &&
        loaded &&
        StringUtils.isEmpty(publication.publishedOn);

    return (
        <EventsProvider source={SearchSource.Bookview}>
            <BookviewSearchProvider>
                <div className={CSS_BASE_CLASS}>
                    <ErrorBoundary>
                        <LayoutGroup>
                            <BreadcrumbsMenu
                                hideBookviewPanelOptions
                                fullWidth={true}>
                                {showBookviewSearchBreadcrumbOptions && (
                                    <>
                                        {inMobileView ? (
                                            <Button
                                                onClick={
                                                    toggleMobileBookviewSearchPanel
                                                }
                                                size={ButtonSizes.Small}
                                                style={
                                                    ButtonStyles.TertiaryAlt
                                                }>
                                                <Icon type={Icons.Search} />
                                                <span className="sr-only">
                                                    {t("find")}
                                                </span>
                                            </Button>
                                        ) : (
                                            <BookviewSearchbar
                                                isLegacyPublicationView
                                            />
                                        )}
                                    </>
                                )}
                            </BreadcrumbsMenu>
                            {BrowserUtils.isIE() ? (
                                <div className={`${CSS_PAGE_CLASS} -legacy`}>
                                    <LegacyPublicationContent
                                        legacyPublication={legacyPublication}
                                        errors={errors}
                                        loading={loading}
                                        isSidebarOpen={
                                            isBookviewSearchPanelOpen &&
                                            isOpen &&
                                            showBookviewSearchBreadcrumbOptions
                                        }
                                    />
                                </div>
                            ) : (
                                <motion.div
                                    className={`${CSS_PAGE_CLASS} -legacy`}
                                    layout>
                                    <LegacyPublicationContent
                                        legacyPublication={legacyPublication}
                                        errors={errors}
                                        loading={loading}
                                        isSidebarOpen={
                                            isBookviewSearchPanelOpen &&
                                            showBookviewSearchBreadcrumbOptions
                                        }
                                    />
                                </motion.div>
                            )}
                        </LayoutGroup>
                    </ErrorBoundary>
                    {renderUnpublishedSticky && (
                        <StickyNotification>
                            {t("unpublished")}
                        </StickyNotification>
                    )}
                </div>
                {inMobileView && isMobileBookviewSearchPanelOpen && (
                    <MobileSecondaryNavLegacyPublications
                        isOpen={isMobileBookviewSearchPanelOpen}
                        toggleOpen={toggleMobileBookviewSearchPanel}
                    />
                )}
            </BookviewSearchProvider>
        </EventsProvider>
    );
};

export default LegacyPublicationPage;
